import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-4" }
const _hoisted_2 = { class: "flex flex-wrap items-center gap-4" }
const _hoisted_3 = { class: "w-[260px]" }
const _hoisted_4 = { class: "flex flex-wrap items-center gap-2" }

import { computed, h, onBeforeUnmount, onMounted, ref, watch, type Component } from 'vue'
import { storeToRefs } from 'pinia'
import {
  mdiFormatListChecks,
  mdiCalendarClock,
  mdiOfficeBuildingOutline,
  mdiCalendarEdit
} from '@mdi/js'

import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import ActiveFilters, { FilterTag } from '@/components/Filters/ActiveFilters.vue'
import SelectFilter from '@/components/Filters/SelectFilter.vue'
import type { CheckValidationStatus } from '@/Network/Types/Responses/CheckValidationResponses'
import {
  CHECK_VALIDATION_PERIOD_OPTIONS,
  CHECK_VALIDATION_STATUS_FILTER_OPTIONS
} from '@/constants/check-validation.constants'
import type { CheckValidationPeriodDays } from '@/constants/check-validation.constants'
import { useHealthUnitStore } from '@/pinia-store/useHealthUnitStore'
import { useScheduleStore } from '@/pinia-store/useScheduleStore'

import { buildCheckValidationFilterTags } from '../filterTags'

const SEARCH_DEBOUNCE_MS = 400

// A barra é CONTROLADA pelo store (props = estado aplicado; emits = intenção):
// assim os chips nunca divergem do que foi de fato para a query, e "limpar"
// não precisa remontar a barra. Só o texto da busca tem estado local (debounce).

export default /*#__PURE__*/_defineComponent({
  __name: 'CheckValidationFilterBar',
  props: {
    search: {},
    statuses: {},
    periodDays: {},
    healthUnitIds: {},
    scheduleIds: {}
  },
  emits: ["search", "filterStatuses", "filterPeriod", "filterUnits", "filterSchedules", "clearFilters"],
  setup(__props: any, { emit }) {

const props = __props;

function mdiIcon(d: string): Component {
  return {
    render: () =>
      h('svg', { class: 'w-4 h-4', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' }, [
        h('path', { d, fill: '#7F7F7F' })
      ])
  }
}

const statusIcon = mdiIcon(mdiFormatListChecks)
const calendarIcon = mdiIcon(mdiCalendarClock)
const hospitalIcon = mdiIcon(mdiOfficeBuildingOutline)
const scheduleIcon = mdiIcon(mdiCalendarEdit)





// `value` é string — contrato do SelectFilter (os emits convertem para number).
const scheduleStore = useScheduleStore()
const { listSchedules } = storeToRefs(scheduleStore)

const healthUnitStore = useHealthUnitStore()
const { listHealthUnits } = storeToRefs(healthUnitStore)

const unitOptions = computed(() =>
  listHealthUnits.value.map((unit) => ({ label: unit.title, value: unit.value }))
)

const scheduleOptions = computed(() =>
  listSchedules.value.map((schedule) => ({ label: schedule.title, value: schedule.value }))
)

const periodOptions = CHECK_VALIDATION_PERIOD_OPTIONS.map((option) => ({
  label: option.label,
  value: String(option.value)
}))

const selectedUnitIds = computed(() => props.healthUnitIds.map(String))
const selectedScheduleIds = computed(() => props.scheduleIds.map(String))

const searchText = ref(props.search)
// Reset externo (clearFilters no store) também limpa o campo.
watch(
  () => props.search,
  (value) => {
    if (value !== searchText.value) searchText.value = value
  }
)

const openFilterName = ref<string | null>(null)

const activeFilterTags = computed(() =>
  buildCheckValidationFilterTags({
    search: props.search,
    statuses: props.statuses,
    onlyOverdue: false,
    periodDays: props.periodDays,
    unitIds: selectedUnitIds.value,
    scheduleIds: selectedScheduleIds.value,
    unitOptions: unitOptions.value,
    scheduleOptions: scheduleOptions.value
  })
)

function handleClickOutside() {
  openFilterName.value = null
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  // Erros já são tratados dentro das actions dos stores de opções.
  void Promise.all([
    scheduleStore.getAllSchedules(),
    healthUnitStore.fetchAllHealthUnitsForSelect()
  ])
})

onBeforeUnmount(() => {
  if (searchDebounce) clearTimeout(searchDebounce)
  document.removeEventListener('click', handleClickOutside)
})

function toggleFilter(name: string) {
  openFilterName.value = openFilterName.value === name ? null : name
}

let searchDebounce: ReturnType<typeof setTimeout> | null = null

function handleSearch() {
  if (searchDebounce) clearTimeout(searchDebounce)
  searchDebounce = setTimeout(() => emit('search', searchText.value), SEARCH_DEBOUNCE_MS)
}

function toggleSelection<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value]
}

// O cast é seguro: os values das opções são os do enum da API.
function handleStatusSelect(value: string) {
  emit('filterStatuses', toggleSelection(props.statuses, value as CheckValidationStatus))
}

function handlePeriodSelect(value: string) {
  emit('filterPeriod', Number(value) as CheckValidationPeriodDays)
}

function handleUnitSelect(value: string) {
  emit('filterUnits', toggleSelection(props.healthUnitIds, Number(value)))
}

function handleScheduleSelect(value: string) {
  emit('filterSchedules', toggleSelection(props.scheduleIds, Number(value)))
}

function clearSearch() {
  // Cancela um debounce em voo: sem isso o timer pendente dispararia um segundo
  // fetch redundante depois da limpeza.
  if (searchDebounce) clearTimeout(searchDebounce)
  searchText.value = ''
}

// O ActiveFilters é genérico (`id: string | number`, `type: string`); o chip
// carrega o id qualificado (`unit-1`) e o SelectFilter trabalha com o id cru.
function handleRemoveTag(tag: FilterTag) {
  const optionId = String(tag.id).replace(`${tag.type}-`, '')

  if (tag.type === 'search') {
    clearSearch()
    emit('search', '')
  } else if (tag.type === 'status') {
    handleStatusSelect(optionId)
  } else if (tag.type === 'period') {
    // Remover o chip de período volta ao preset default — sem período a fila
    // traria o histórico inteiro, que não é o que "remover" quer dizer aqui.
    emit('filterPeriod', CHECK_VALIDATION_PERIOD_OPTIONS[0].value)
  } else if (tag.type === 'unit') {
    handleUnitSelect(optionId)
  } else if (tag.type === 'schedule') {
    handleScheduleSelect(optionId)
  }
}

// Delega ao pai o reset do store num único fetch — remover chip por chip
// dispararia uma request por filtro.
function handleClearAll() {
  clearSearch()
  openFilterName.value = null
  emit('clearFilters')
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(SearchInput, {
          modelValue: searchText.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchText).value = $event)),
          "data-testid": "check-validation-search-input",
          label: "Buscar por médico ou CRM",
          width: "100%",
          "hide-details": "",
          density: "compact",
          onInput: handleSearch
        }, null, 8, ["modelValue"])
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", {
          class: "relative",
          onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (toggleFilter('status')), ["stop"]))
        }, [
          _createVNode(SelectFilter, {
            "data-testid": "check-validation-status-filter",
            label: "Status",
            "is-open": openFilterName.value === 'status',
            options: _unref(CHECK_VALIDATION_STATUS_FILTER_OPTIONS),
            icon: _unref(statusIcon),
            "selected-value": _ctx.statuses,
            "dropdown-width": "w-64",
            multiple: "",
            onSelect: handleStatusSelect
          }, null, 8, ["is-open", "options", "icon", "selected-value"])
        ]),
        _createElementVNode("div", {
          class: "relative",
          onClick: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (toggleFilter('period')), ["stop"]))
        }, [
          _createVNode(SelectFilter, {
            "data-testid": "check-validation-period-filter",
            label: "Período",
            "is-open": openFilterName.value === 'period',
            options: _unref(periodOptions),
            icon: _unref(calendarIcon),
            "selected-value": _ctx.periodDays === null ? null : String(_ctx.periodDays),
            "dropdown-width": "w-64",
            onSelect: handlePeriodSelect
          }, null, 8, ["is-open", "options", "icon", "selected-value"])
        ]),
        _createElementVNode("div", {
          class: "relative",
          onClick: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (toggleFilter('unit')), ["stop"]))
        }, [
          _createVNode(SelectFilter, {
            "data-testid": "check-validation-unit-filter",
            label: "Unidades",
            "is-open": openFilterName.value === 'unit',
            options: unitOptions.value,
            icon: _unref(hospitalIcon),
            "selected-value": selectedUnitIds.value,
            "dropdown-width": "w-64",
            searchable: "",
            multiple: "",
            onSelect: handleUnitSelect
          }, null, 8, ["is-open", "options", "icon", "selected-value"])
        ]),
        _createElementVNode("div", {
          class: "relative",
          onClick: _cache[4] || (_cache[4] = _withModifiers(($event: any) => (toggleFilter('schedule')), ["stop"]))
        }, [
          _createVNode(SelectFilter, {
            "data-testid": "check-validation-schedule-filter",
            label: "Escala",
            "is-open": openFilterName.value === 'schedule',
            options: scheduleOptions.value,
            icon: _unref(scheduleIcon),
            "selected-value": selectedScheduleIds.value,
            "dropdown-width": "w-64",
            searchable: "",
            multiple: "",
            onSelect: handleScheduleSelect
          }, null, 8, ["is-open", "options", "icon", "selected-value"])
        ])
      ])
    ]),
    _createVNode(ActiveFilters, {
      filters: activeFilterTags.value,
      "show-clear-button": activeFilterTags.value.length > 0,
      "test-id-prefix": "check-validation-active-filters",
      onRemove: handleRemoveTag,
      onClearAll: handleClearAll
    }, null, 8, ["filters", "show-clear-button"])
  ]))
}
}

})